import { Check, X } from 'lucide-react';
import { Button } from '../actions/Button';
import type { BillingSubscription } from '../../types';

interface PlanChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  subscription: BillingSubscription;
  isLoading: boolean;
}

export function PlanChangeModal({
  isOpen,
  onClose,
  onConfirm,
  subscription,
  isLoading,
}: PlanChangeModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-background-card border border-border rounded-xl max-w-md w-full p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                Subscribe to {subscription.name}
              </h3>
              <p className="text-sm text-text-secondary">
                £{subscription.price_yearly}/year
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Info */}
        <div className="space-y-4 mb-6">
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm text-text-primary mb-2">
              <strong>Start Your Subscription</strong>
            </p>
            <p className="text-sm text-text-secondary">
              You'll be redirected to Stripe checkout to securely enter your payment details and start your subscription.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Plan:</span>
              <span className="font-semibold text-text-primary">{subscription.name}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Price:</span>
              <span className="font-semibold text-text-primary">
                £{subscription.price_yearly}/year
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Publishing:</span>
              <span className="font-semibold text-text-primary">Up to 10 live invites</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            fullWidth
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="primary"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Continue to Checkout'}
          </Button>
        </div>
      </div>
    </div>
  );
}
